/**
 * @module app.cm
 */

import { autoinject, PLATFORM } from 'aurelia-framework';
import { Router, RouterConfiguration, RouteConfig } from 'aurelia-router';
import { IAppRouteConfig } from 'app/shared/config/router-types';
import { AccountContext } from 'app/core/lib/account-context';


@autoinject()
export class ManageCasesPage {

    ////****************** Fields and Properties ************************************////
    protected router: Router | null = null;
    protected isCaseAttachToDocument: boolean = false;
    protected params: any;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly accountContext: AccountContext) { }

    protected async canActivate(params: any, routeConfig: RouteConfig): Promise<void> {
        this.params = params;

        this.isCaseAttachToDocument = params.faxDocumentId != null;
    }

    protected attached(): void {
        this.checkIfCorrectRoute();
    }

    ////****************** Functions ************************************************////
    protected async configureRouter(config: RouterConfiguration, router: Router): Promise<void> {
        await this.accountContext.load();

        const root = router.generate('cm.manage-cases');
        const routes: IAppRouteConfig[] = [
            { route: [''], redirect: 'search' },
            // Tab Navigation
            {
                route: 'household/create/:householdId?', href: `${root}/household/create`, name: 'cm.manage-cases.household.create', moduleId: PLATFORM.moduleName('app/cm/pages/manage-cases/household/create-household-case-page'),
                title: 'cm.manage-cases.household.create', nav: true, activationStrategy: 'invoke-lifecycle',
                settings: { icon: 'ui:icons.create', isVisible: this.accountContext.hasRouteAccess('cm.manage-cases.household.create'), topic: 'cm.manage-cases.household.create' }
            },
            {
                route: 'create/:memberId?', href: `${root}/create`, name: 'cm.manage-cases.create', moduleId: PLATFORM.moduleName('app/cm/pages/manage-cases/create-case-page'),
                title: 'cm.manage-cases.create', nav: true, activationStrategy: 'invoke-lifecycle',
                settings: { icon: 'ui:icons.create', isVisible: this.accountContext.hasRouteAccess('cm.manage-cases.create'), topic: 'cm.manage-cases.create' }
            },
            {
                route: 'search', name: 'cm.manage-cases.search', moduleId: PLATFORM.moduleName('app/cm/pages/manage-cases/case-search-page'),
                title: 'cm.manage-cases.search', nav: true, activationStrategy: 'invoke-lifecycle',
                settings: { icon: 'ui:icons.search', isVisible: this.accountContext.hasRouteAccess('cm.manage-cases.search'), topic: 'cm.manage-cases.search' }
            },
            {
                route: 'tasks-interventions', name: 'cm.manage-cases.tasks-interventions', moduleId: PLATFORM.moduleName('app/cm/pages/manage-cases/tasks-interventions-page'),
                title: 'cm.manage-cases.tasks-interventions', nav: true,
                settings: { icon: 'ui:icons.tasks', isVisible: this.accountContext.hasRouteAccess('cm.manage-cases.tasks-interventions'), topic: 'cm.manage-cases.tasks-interventions' }
            },
            {
                route: 'appointments', name: 'cm.manage-cases.appointments', moduleId: PLATFORM.moduleName('app/cm/pages/manage-cases/appointments-page'),
                title: 'cm.manage-cases.appointments', nav: true,
                settings: { icon: 'ui:icons.appointment', isVisible: this.accountContext.hasRouteAccess('cm.manage-cases.appointments'), topic: 'cm.manage-cases.appointments' }
            },
            {
                route: 'calendar', name: 'cm.manage-cases.calendar', moduleId: PLATFORM.moduleName('app/cm/pages/manage-cases/calendar-page'),
                title: 'cm.manage-cases.calendar', nav: true,
                settings: { icon: 'ui:icons.calendar', isVisible: this.accountContext.hasRouteAccess('cm.manage-cases.calendar'), topic: 'cm.manage-cases.calendar' }, activationStrategy: 'replace'
            },
            {
                route: 'assign', name: 'cm.manage-cases.assign', moduleId: PLATFORM.moduleName('app/cm/pages/manage-cases/assign-cases-page'),
                title: 'cm.manage-cases.assign', nav: true,
                settings: { icon: 'ui:icons.assign', isVisible: this.accountContext.hasRouteAccess('cm.manage-cases.assign'), topic: 'cm.manage-cases.assign' }
            }
        ];

        config.map(routes);

        config.mapUnknownRoutes('app/shared/components/not-found');
        this.router = router;
    }

    protected checkIfCorrectRoute() {
        this.params = this.router?.currentInstruction?.queryParams;

        this.isCaseAttachToDocument = this.params.faxDocumentId != null;

        // If attaching existing document to case, make sure only the create case page is active
        if(this.isCaseAttachToDocument && this.router?.currentInstruction?.config?.name !== 'cm.manage-cases.create') {
            this.router!.navigateToRoute('cm.manage-cases.create', this.params, { replace: true, trigger: true });
        }
    }

    protected reset(href: string): true | void {
        if(href == location.hash) {
            this.router!.navigate(href, { replace: true, trigger: true });
        }
        else return true;
    }

}
